/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
import React from "react";
import {
  X,
  RotateCcw,
  Loader,
  User,
  Briefcase,
  AlertCircle,
  CheckCircle2,
  PlayCircle,
  Flag,
  Ban,
  MapPin,
} from "lucide-react";
import LazyImage from "./LazyImage";

const BookingModal = ({
  selectedBooking,
  showModal,
  statusChanging,
  currentAction,
  reminder,
  setReminder,
  onClose,
  onStatusChange,
  onReplaceCaregiver,
}) => {
  if (!showModal || !selectedBooking) return null;

  const statusConfig = {
    New: {
      icon: <Flag size={16} className="text-primary" />,
      bgColor: "bg-primary/10 dark:bg-primary/20",
      borderColor: "border-primary/20 dark:border-primary/30",
      textColor: "text-primary dark:text-primary",
      label: "New Booking",
    },
    "In Progress": {
      icon: (
        <PlayCircle size={16} className="text-amber-600 dark:text-amber-400" />
      ),
      bgColor: "bg-amber-50 dark:bg-amber-900/20",
      borderColor: "border-amber-200 dark:border-amber-800",
      textColor: "text-amber-700 dark:text-amber-300",
      label: "In Progress",
    },
    "Work Started": {
      icon: (
        <Briefcase size={16} className="text-secondary dark:text-blue-400" />
      ),
      bgColor: "bg-blue-50 dark:bg-secondary/20",
      borderColor: "border-blue-200 dark:border-secondary/30",
      textColor: "text-secondary dark:text-blue-300",
      label: "Work Started",
    },
    Completed: {
      icon: (
        <CheckCircle2
          size={16}
          className="text-emerald-600 dark:text-emerald-400"
        />
      ),
      bgColor: "bg-emerald-50 dark:bg-emerald-900/20",
      borderColor: "border-emerald-200 dark:border-emerald-800",
      textColor: "text-emerald-700 dark:text-emerald-300",
      label: "Completed",
    },
    Cancelled: {
      icon: <Ban size={16} className="text-red-600 dark:text-red-400" />,
      bgColor: "bg-red-50 dark:bg-red-900/20",
      borderColor: "border-red-200 dark:border-red-800",
      textColor: "text-red-700 dark:text-red-300",
      label: "Cancelled",
    },
  };

  // The API uses enum values such as `Work_Started`, while the UI labels use spaces.
  const bookingStatus = String(selectedBooking.status || "New")
    .trim()
    .replace(/[_\s]+/g, " ")
    .toLowerCase();

  const currentStatus =
    {
      new: statusConfig.New,
      "in progress": statusConfig["In Progress"],
      "work started": statusConfig["Work Started"],
      completed: statusConfig.Completed,
      cancelled: statusConfig.Cancelled,
    }[bookingStatus] || statusConfig.New;

  const renderWorkflowActions = () => {
    if (!selectedBooking) return null;

    const actions = [];

    if (bookingStatus === "new") {
      actions.push({
        key: "in-progress",
        label: "Start In Progress",
        loadingLabel: "Starting...",
        action: () => onStatusChange("In_Progress"),
        variant: "primary",
        icon: <PlayCircle size={18} />,
      });
    } else if (bookingStatus === "in progress") {
      actions.push({
        key: "work-started",
        label: "Mark Work Started",
        loadingLabel: "Updating...",
        action: () => onStatusChange("Work_Started"),
        variant: "primary",
        icon: <Briefcase size={18} />,
      });
    } else if (bookingStatus === "work started") {
      actions.push({
        key: "completed",
        label: "Complete Job",
        loadingLabel: "Completing...",
        action: () => onStatusChange("Completed"),
        variant: "success",
        icon: <CheckCircle2 size={18} />,
      });
    }

    // Secondary actions
    if (!["completed", "cancelled"].includes(bookingStatus)) {
      actions.push({
        key: "replace-caregiver",
        label: "Replace Caregiver",
        loadingLabel: "Processing...",
        action: onReplaceCaregiver,
        variant: "secondary",
        icon: <RotateCcw size={18} />,
      });
      actions.push({
        key: "cancelled",
        label: "Cancel Booking",
        loadingLabel: "Cancelling...",
        action: () => onStatusChange("Cancelled"),
        variant: "danger",
        icon: <Ban size={18} />,
      });
    }

    const getButtonClasses = (variant, isDisabled) => {
      const baseClasses =
        "w-full sm:flex-1 px-4 py-3.5 sm:py-3 rounded-lg font-semibold text-sm sm:text-base transition-all duration-200 flex items-center justify-center gap-2 border min-h-[48px] sm:min-h-[44px]";

      if (isDisabled) {
        return `${baseClasses} opacity-50 cursor-not-allowed bg-accent text-text/40 border-accent dark:bg-dark-surface dark:text-dark-muted dark:border-dark-border`;
      }

      switch (variant) {
        case "primary":
          return `${baseClasses} bg-primary text-white border-primary active:bg-primary/90 sm:hover:bg-primary/90 shadow-sm`;
        case "success":
          return `${baseClasses} bg-emerald-600 text-white border-emerald-600 active:bg-emerald-700 sm:hover:bg-emerald-700 shadow-sm`;
        case "secondary":
          return `${baseClasses} bg-card text-text border-accent active:bg-accent sm:hover:bg-accent dark:bg-dark-surface dark:text-dark-text dark:border-dark-border dark:active:bg-dark-border sm:dark:hover:bg-dark-border`;
        case "danger":
          return `${baseClasses} bg-card text-red-600 border-red-200 active:bg-red-50 sm:hover:bg-red-50 dark:bg-red-900/10 dark:text-red-400 dark:border-red-800 dark:active:bg-red-900/20 sm:dark:hover:bg-red-900/20`;
        default:
          return baseClasses;
      }
    };

    return (
      <div className="space-y-3 pt-2">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {actions.map((action) => (
            <button
              key={action.key}
              onClick={action.action}
              disabled={statusChanging}
              className={getButtonClasses(action.variant, statusChanging)}
            >
              {statusChanging &&
              currentAction ===
                (action.label === "Replace Caregiver"
                  ? "Replace Caregiver"
                  : action.label) ? (
                <>
                  <Loader size={18} className="animate-spin" />
                  <span>{action.loadingLabel}</span>
                </>
              ) : (
                <>
                  <span>{action.label}</span>
                </>
              )}
            </button>
          ))}
        </div>
        {statusChanging && (
          <div className="flex items-center justify-center gap-2 text-sm text-text/60 dark:text-dark-muted bg-accent dark:bg-dark-surface py-3 px-3 rounded-md">
            <Loader size={16} className="animate-spin" />
            <span>Processing your request...</span>
          </div>
        )}
      </div>
    );
  };

  // Format date for better display
  const formatDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return { date: "N/A", time: "" };
    try {
      const date = new Date(dateTimeStr);
      return {
        date: date.toLocaleDateString("en-US", {
          weekday: "short",
          year: "numeric",
          month: "short",
          day: "numeric",
        }),
        time: date.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
        }),
      };
    } catch {
      return { date: dateTimeStr, time: "" };
    }
  };

  const dateTime = formatDateTime(selectedBooking.dateTime);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60 backdrop-blur-sm overflow-hidden">
      <div className="bg-card dark:bg-dark-surface w-full sm:rounded-2xl sm:shadow-2xl sm:max-w-2xl sm:mx-auto h-[100dvh] sm:h-auto sm:max-h-[90vh] flex flex-col overflow-hidden border-0 sm:border border-accent dark:border-dark-border">
        {/* Header */}
        <div className="px-4 sm:px-6 py-4 border-b border-accent dark:border-dark-border flex justify-between items-center bg-background dark:bg-dark-background flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="min-w-0">
              <h2 className="text-base sm:text-lg font-bold text-text dark:text-dark-text truncate">
                Booking Details
              </h2>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={statusChanging}
            className={`p-2 rounded-xl transition-colors flex-shrink-0 ${
              statusChanging
                ? "opacity-50 cursor-not-allowed text-text/40"
                : "text-text/60 hover:text-text hover:bg-accent dark:text-dark-muted dark:hover:text-dark-text dark:hover:bg-dark-surface"
            }`}
          >
            <X size={24} />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto overscroll-contain bg-background dark:bg-dark-background">
          <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
            {/* Status Banner */}
            <div
              className={`flex items-center gap-3 p-3 sm:p-4 rounded-xl border ${currentStatus.bgColor} ${currentStatus.borderColor}`}
            >
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-card dark:bg-dark-surface flex items-center justify-center shadow-sm flex-shrink-0">
                {currentStatus.icon}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-text/60 dark:text-dark-muted uppercase tracking-wider mb-0.5">
                  Current Status
                </p>
                <p
                  className={`text-sm sm:text-base font-bold ${currentStatus.textColor} truncate`}
                >
                  {currentStatus.label}
                </p>
              </div>
            </div>

            {/* Parties Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Client Card */}
              <div className="bg-card dark:bg-dark-surface rounded-xl p-4 border border-accent dark:border-dark-border shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="relative flex-shrink-0">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-background dark:bg-dark-background border-2 border-accent dark:border-dark-border overflow-hidden shadow-sm">
                      <LazyImage
                        src={selectedBooking.client.avatar}
                        alt="Client"
                        className="w-full h-full object-cover"
                        fallback={
                          <div className="w-full h-full bg-primary/10 dark:bg-primary/20 flex items-center justify-center">
                            <User size={28} className="text-primary" />
                          </div>
                        }
                      />
                    </div>
                    <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-primary rounded-full flex items-center justify-center border-2 border-card dark:border-dark-surface">
                      <User size={12} className="text-white" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-text/60 dark:text-dark-muted uppercase tracking-wider mb-0.5">
                      Client
                    </p>
                    <h3 className="text-base sm:text-lg font-bold text-text dark:text-dark-text truncate">
                      {selectedBooking.client.name}
                    </h3>
                    {selectedBooking.client.email && (
                      <p className="text-xs sm:text-sm text-text/60 dark:text-dark-muted truncate mt-0.5">
                        {selectedBooking.client.email}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Caregiver Card */}
              <div className="bg-card dark:bg-dark-surface rounded-xl p-4 border border-accent dark:border-dark-border shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="relative flex-shrink-0">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-background dark:bg-dark-background border-2 border-accent dark:border-dark-border overflow-hidden shadow-sm">
                      <LazyImage
                        src={selectedBooking.caregiver.avatar}
                        alt="Caregiver"
                        className="w-full h-full object-cover"
                        fallback={
                          <div className="w-full h-full bg-secondary/10 dark:bg-secondary/20 flex items-center justify-center">
                            <User
                              size={28}
                              className="text-secondary dark:text-blue-400"
                            />
                          </div>
                        }
                      />
                    </div>
                    <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-secondary rounded-full flex items-center justify-center border-2 border-card dark:border-dark-surface">
                      <Briefcase size={12} className="text-white" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-text/60 dark:text-dark-muted uppercase tracking-wider mb-0.5">
                      Caregiver
                    </p>
                    <h3 className="text-base sm:text-lg font-bold text-text dark:text-dark-text truncate">
                      {selectedBooking.caregiver.name}
                    </h3>
                    {selectedBooking.caregiver.specialty && (
                      <p className="text-xs sm:text-sm text-text/60 dark:text-dark-muted truncate mt-0.5">
                        {selectedBooking.caregiver.specialty}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Details Grid  */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              <div className="bg-card dark:bg-dark-surface rounded-xl p-3 sm:p-4 border border-accent dark:border-dark-border shadow-sm">
                <div className="flex items-center gap-2 mb-1.5">
                  <p className="text-xs font-bold text-text/60 dark:text-dark-muted uppercase tracking-wider">
                    Service
                  </p>
                </div>
                <p className="text-sm sm:text-base font-bold text-text dark:text-dark-text leading-tight">
                  {selectedBooking.serviceType}
                </p>
              </div>

              <div className="bg-card dark:bg-dark-surface rounded-xl p-3 sm:p-4 border border-accent dark:border-dark-border shadow-sm">
                <div className="flex items-center gap-2 mb-1.5">
                  <p className="text-xs font-bold text-text/60 dark:text-dark-muted uppercase tracking-wider">
                    Date
                  </p>
                </div>
                <p className="text-sm sm:text-base font-bold text-text dark:text-dark-text leading-tight">
                  {dateTime.date}
                </p>
                {dateTime.time && (
                  <p className="text-xs sm:text-sm text-text/60 dark:text-dark-muted mt-0.5">
                    {dateTime.time}
                  </p>
                )}
              </div>

              <div className="bg-card dark:bg-dark-surface rounded-xl p-3 sm:p-4 border border-accent dark:border-dark-border shadow-sm">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-xs font-bold text-text/60 dark:text-dark-muted uppercase tracking-wider">
                    Salary
                  </span>
                </div>
                <p className="text-sm sm:text-base font-bold text-text dark:text-dark-text leading-tight">
                  Br {selectedBooking.salary}
                </p>
              </div>
            </div>

            {/* Location if available */}
            {selectedBooking.location && (
              <div className="bg-card dark:bg-dark-surface rounded-xl p-3 sm:p-4 border border-accent dark:border-dark-border shadow-sm">
                <div className="flex items-start gap-2">
                  <MapPin
                    size={16}
                    className="text-text/40 dark:text-dark-muted flex-shrink-0 mt-0.5"
                  />
                  <div>
                    <p className="text-xs font-bold text-text/60 dark:text-dark-muted uppercase tracking-wider mb-1">
                      Location
                    </p>
                    <p className="text-sm sm:text-base text-text dark:text-dark-text">
                      {selectedBooking.location}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Reminder Section */}
            <div className="space-y-2">
              <label className="text-sm font-bold text-text dark:text-dark-text flex items-center gap-2">
                <AlertCircle
                  size={18}
                  className="text-text/40 dark:text-dark-muted"
                />
                Internal Notes
              </label>
              <textarea
                disabled={statusChanging}
                className={`w-full p-3 sm:p-4 rounded-xl border bg-card dark:bg-dark-surface text-text dark:text-dark-text border-accent dark:border-dark-border placeholder-text/40 dark:placeholder-dark-muted text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none min-h-[100px] sm:min-h-[80px] ${
                  statusChanging ? "opacity-50 cursor-not-allowed" : ""
                }`}
                placeholder="Add internal notes or reminders..."
                value={reminder}
                onChange={(e) => setReminder(e.target.value)}
                rows={3}
              />
            </div>

            {/* Workflow Actions */}
            {renderWorkflowActions()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingModal;
